import {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useSnackbar} from 'notistack';
import axios from 'axios';
import {NotificationApi} from '../api/notificationApi';
import {EmailItem, NotificationItem} from '../types';
import {
  RECEIVE_MAIL_TYPE,
  ReceiveMailType,
  normalizeReceiveMailType
} from '../constants/alertSettingsConfig';

interface UseNotificationSettingsParams {
  alertId: string;
  open: boolean;
  emails: EmailItem[];
  reload: () => Promise<void>;
}

export const useNotificationSettings = ({
  alertId,
  open,
  emails,
  reload
}: UseNotificationSettingsParams) => {
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // 通知先追加ダイアログ用
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newReceiveMailType, setNewReceiveMailType] = useState<ReceiveMailType>(
    RECEIVE_MAIL_TYPE.ALL
  );

  // バッチ操作用
  const [selectedIndices, setSelectedIndices] = useState<number[]>([]);

  // 初期データロード
  useEffect(() => {
    if (open && alertId !== '-1') {
      loadNotifications();
    }
  }, [open, alertId]);

  const loadNotifications = async () => {
    setIsLoading(true);
    const CancelToken = axios.CancelToken;
    const source = CancelToken.source();

    try {
      // NotificationApi を使用してアラート設定データを取得
      const data = await NotificationApi.getNotifications(
        alertId,
        enqueueSnackbar,
        source.token,
        t
      );

      // データの存在チェック
      if (!data || !data[0]) {
        console.warn('アラート設定データが取得できませんでした');
        setNotifications([]);
        return;
      }

      const alertData = data[0];

      // recipient_details から通知先を抽出
      if (Array.isArray(alertData.recipient_details)) {
        const newNotifications = alertData.recipient_details
          .map((item: any) => {
            if (!item || !item.recipients) return null;

            // emailsリストからuser_idを検索
            const emailData = emails.find(e => e.id === item.recipients);
            if (!emailData) {
              console.warn(`Email not found for recipient: ${item.recipients}`);
              return null;
            }

            return {
              email: emailData.id,
              receiveMailType: normalizeReceiveMailType(item.receive_mail)
            };
          })
          .filter((item: any) => item !== null)
          .sort((a: any, b: any) => a.email.localeCompare(b.email));

        setNotifications(newNotifications);
      } else {
        setNotifications([]);
      }
    } catch (error) {
      console.error('Failed to load notifications:', error);
      enqueueSnackbar(t('通知先の取得に失敗しました'), {variant: 'error'});
      setNotifications([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInlineSendSettingChange = (index: number, receiveMailType: ReceiveMailType) => {
    const updated = notifications.map((n, i) => (i === index ? {...n, receiveMailType} : n));
    setNotifications(updated);
  };

  const handleInlineDelete = (index: number) => {
    const updated = notifications.filter((_, i) => i !== index);
    setNotifications(updated);
    enqueueSnackbar(t('通知先を削除しました'), {variant: 'success'});
  };

  const handleInlineAdd = () => {
    if (!newEmail) {
      enqueueSnackbar(t('メールアドレスを選択してください'), {variant: 'error'});
      return;
    }

    const emailData = emails.find(e => e.email === newEmail);
    if (!emailData) {
      enqueueSnackbar(t('メールアドレスが存在しません'), {variant: 'error'});
      return;
    }

    if (notifications.some(n => n.email === emailData.id)) {
      enqueueSnackbar(t('既に登録されている通知先です'), {variant: 'error'});
      return;
    }

    const updated = [...notifications, {email: emailData.id, receiveMailType: newReceiveMailType}];
    setNotifications(updated);
    enqueueSnackbar(t('通知先を追加しました'), {variant: 'success'});
    setAddDialogOpen(false);
  };

  const handleOpenAddDialog = () => {
    setNewEmail('');
    setNewReceiveMailType(RECEIVE_MAIL_TYPE.ALL);
    setAddDialogOpen(true);
  };

  const handleCloseAddDialog = () => {
    setAddDialogOpen(false);
  };

  const handleToggleSelect = (index: number) => {
    setSelectedIndices(prev =>
      prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
    );
  };

  const handleToggleSelectAll = () => {
    if (selectedIndices.length === notifications.length) {
      setSelectedIndices([]);
    } else {
      setSelectedIndices(notifications.map((_, i) => i));
    }
  };

  const handleBatchDelete = () => {
    const updated = notifications.filter((_, i) => !selectedIndices.includes(i));
    setNotifications(updated);
    setSelectedIndices([]);
    enqueueSnackbar(t('選択した通知先を削除しました'), {variant: 'success'});
  };

  const handleBatchSendSettingChange = (receiveMailType: ReceiveMailType) => {
    const updated = notifications.map((n, i) =>
      selectedIndices.includes(i) ? {...n, receiveMailType} : n
    );
    setNotifications(updated);
    enqueueSnackbar(t('送信設定を変更しました'), {variant: 'success'});
  };

  // 保存処理
  const onSave = async () => {
    const CancelToken = axios.CancelToken;
    const source = CancelToken.source();

    setIsLoading(true);
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('[onSave] 保存開始:', {
          alertId,
          notificationCount: notifications.length,
          notifications: notifications.map(n => ({
            email: n.email,
            receiveMailType: n.receiveMailType
          }))
        });
      }

      // Step 1: 既存の通知先を取得
      const existingData = await NotificationApi.getNotifications(
        alertId,
        enqueueSnackbar,
        source.token,
        t
      );

      if (process.env.NODE_ENV === 'development') {
        console.log('[onSave] 既存の通知先:', existingData);
      }

      // Step 2: 既存の通知先を削除（並列実行）
      if (existingData && existingData[0] && Array.isArray(existingData[0].recipient_details)) {
        const deletePromises = existingData[0].recipient_details
          .filter(item => item && item.recipients)
          .map(item => {
            if (process.env.NODE_ENV === 'development') {
              console.log('[onSave] 通知先削除:', item.recipients);
            }

            return NotificationApi.deleteNotification(
              alertId,
              item.recipients,
              enqueueSnackbar,
              source.token,
              t
            );
          });

        await Promise.all(deletePromises);
      }

      // Step 3: 新しい通知先を登録（並列実行）
      const addPromises = notifications
        .map((notification, i) => {
          if (process.env.NODE_ENV === 'development') {
            console.log(`[onSave] 通知先登録 ${i + 1}/${notifications.length}:`, {
              alert_id: alertId,
              recipients: notification.email,
              receive_mail: notification.receiveMailType
            });
          }

          return NotificationApi.addNotification(
            alertId,
            notification.email,
            notification.receiveMailType,
            enqueueSnackbar,
            source.token,
            t
          );
        })
        .filter(promise => promise !== null);

      await Promise.all(addPromises);

      if (process.env.NODE_ENV === 'development') {
        console.log('[onSave] 保存完了');
      }

      enqueueSnackbar(t('通知先を保存しました'), {variant: 'success'});
      await reload();
    } catch (error) {
      console.error('[onSave] 通知先保存エラー:', error);

      if (axios.isCancel(error)) {
        console.warn('[onSave] リクエストがキャンセルされました');
        enqueueSnackbar(t('処理がキャンセルされました'), {variant: 'warning'});
      } else {
        const errorMessage = error instanceof Error ? error.message : '不明なエラー';
        console.error('[onSave] エラー詳細:', {
          message: errorMessage,
          error
        });
        enqueueSnackbar(t('通知先の保存に失敗しました') + ': ' + errorMessage, {
          variant: 'error',
          persist: true
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return {
    notifications,
    isLoading,
    handleInlineSendSettingChange,
    handleInlineDelete,
    handleInlineAdd,
    newEmail,
    setNewEmail,
    newReceiveMailType,
    setNewReceiveMailType,
    addDialogOpen,
    handleOpenAddDialog,
    handleCloseAddDialog,
    selectedIndices,
    handleToggleSelect,
    handleToggleSelectAll,
    handleBatchDelete,
    handleBatchSendSettingChange,
    onSave
  };
};
